import { Dialog } from '@headlessui/react';
import { useRichTextEditor } from '@shared/rich-text-editor-core';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Modal, Button, Form } from '../../base';
import { TableChartOutlinedIcon } from '../../icons';

import ControlButton from './control-button';

export interface TableInsertModalProps {
  open: boolean;
  defaultRows?: number;
  defaultCols?: number;
  onCancel: () => void;
  onInsert: (rows: number, cols: number) => void;
}

export function TableInsertModal(props: TableInsertModalProps) {
  const { open, defaultRows = 3, defaultCols = 3, onCancel, onInsert } = props;
  const { control, getValues, reset } = useForm<{ rows: number; cols: number }>({
    defaultValues: { rows: defaultRows, cols: defaultCols },
  });

  useEffect(() => {
    if (open) {
      reset({ rows: defaultRows, cols: defaultCols });
    }
  }, [open, defaultRows, defaultCols, reset]);

  if (!open) return null;

  return (
    <Modal open={open} onClose={onCancel} className='w-full max-w-5xl rounded-2xl p-0'>
      <div className='relative px-8 py-6 md:px-10 md:py-8'>
        <Dialog.Title as='h3'>Insert Table</Dialog.Title>
      </div>
      <div className='px-8 py-8'>
        <div className='grid grid-cols-2 gap-8'>
          <div className='flex flex-col gap-3'>
            <span className='text-xl font-semibold text-neutral-800'>Rows</span>
            <Form.Input control={control} name='rows' type='number' min={1} />
          </div>
          <div className='flex flex-col gap-3'>
            <span className='text-xl font-semibold text-neutral-800'>Columns</span>
            <Form.Input control={control} name='cols' type='number' min={1} />
          </div>
        </div>
      </div>
      <div className='flex justify-end gap-2 px-8 pb-8'>
        <Button variant='text' onClick={onCancel}>
          Cancel
        </Button>
        <Button
          onClick={() => {
            const r = Math.max(1, Math.floor(Number(getValues('rows')) || 0));
            const c = Math.max(1, Math.floor(Number(getValues('cols')) || 0));
            onInsert(r, c);
          }}
        >
          Insert
        </Button>
      </div>
    </Modal>
  );
}

function TableControl() {
  const { editor } = useRichTextEditor();
  const [open, setOpen] = useState(false);

  if (!editor) return null;

  const handleInsert = (rows: number, cols: number) => {
    const r = Math.max(1, Math.floor(rows));
    const c = Math.max(1, Math.floor(cols));
    editor.chain().focus().insertTable({ rows: r, cols: c, withHeaderRow: false }).run();
    setOpen(false);
  };

  return (
    <>
      <ControlButton onClick={() => setOpen(true)}>
        <TableChartOutlinedIcon className='h-6 w-6 text-2xl' />
      </ControlButton>
      <TableInsertModal
        open={open}
        defaultRows={3}
        defaultCols={3}
        onCancel={() => setOpen(false)}
        onInsert={handleInsert}
      />
    </>
  );
}

export default TableControl;
export { TableControl as RichTextEditorTableControl };
